import { ChangeDetectorRef, Component, PLATFORM_ID, OnInit, AfterViewInit, OnDestroy, inject } from '@angular/core';
import { CookieConstent } from '../../../services/cookie-constent/cookie-constent';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MailService } from '../../../services/mail-service/mail-service';
import { RouterLink } from '@angular/router';
import { FadeInReveal } from '../../../directives/fade-in-reveal';
import { BottomTransitionReveal } from '../../../directives/bottom-transition-reveal';

export enum ComponentState {
  Form = 'form',
  Loading = 'loading',
  Success = 'success',
  Error = 'error'
}

interface GoogleWindow extends Window {
  google?: typeof google;
}

interface ScriptWithPromise extends HTMLScriptElement {
  _gmapsReadyPromise?: Promise<void>;
}

@Component({
  selector: 'app-contact',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    FadeInReveal,
    BottomTransitionReveal
  ],
  templateUrl: './contact.html',
  styleUrl: './contact.less'
})
export class Contact implements OnInit, AfterViewInit, OnDestroy {

  showMap = false;
  rejectedCookies = false;

  ComponentState = ComponentState;
  currentState = ComponentState.Form;

  contactForm = new FormGroup({
    name: new FormControl('', Validators.required),
    email: new FormControl('', [Validators.required, Validators.email]),
    phone: new FormControl(''),
    message: new FormControl('', Validators.required),
    terms: new FormControl(false, Validators.requiredTrue),
  });

  private cookieConsentService = inject(CookieConstent);
  private mailService = inject(MailService);
  private platformId = inject(PLATFORM_ID);
  private changeDetector = inject(ChangeDetectorRef);
  private isBrowser = isPlatformBrowser(this.platformId);

  private acceptHandler = (): void => {
    this.showMap = true;
    this.ensureMapsReady()
      .then(() => this.loadGoogleMap())
      .catch((err: unknown) => console.error('Error cargando Google Maps:', err));
    this.changeDetector.detectChanges();
  };

  private declineHandler = (): void => {
    this.showMap = false;
    this.rejectedCookies = true;
    this.changeDetector.detectChanges();
  };

  private clickHandler = (event: MouseEvent): void => {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (target.innerText === 'Aceptar') {
      this.acceptHandler();
    } else if (target.innerText === 'Rechazar') {
      this.declineHandler();
    }
  };

  ngOnInit(): void {
    if (this.isBrowser) {
      this.showMap = localStorage.getItem('userConsentDecision') === 'accepted';
      this.rejectedCookies = localStorage.getItem('userConsentDecision') === 'rejected';
      window.addEventListener('click', this.clickHandler);

      if (this.showMap) {
        this.ensureMapsReady().catch((err: unknown) => console.error('Error inicial cargando Google Maps:', err));
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      setTimeout(() => {
        this.cookieConsentService.initializeCookieConsent();
      }, 350);

      setTimeout(() => {
        if (this.showMap) {
          this.ensureMapsReady()
            .then(() => this.loadGoogleMap())
            .catch((err: unknown) => console.error('Error cargando Google Maps:', err));
        }
      }, 100);
    }
  }

  ngOnDestroy(): void {
    if (this.isBrowser) {
      window.removeEventListener('click', this.clickHandler);
    }
  }

  reopenConsent(): void {
    this.cookieConsentService.showConsentModal();
  }

  submitForm(): void {
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }
    this.currentState = ComponentState.Loading;
    this.contactForm.disable();

    this.mailService.sendEmail(
      this.contactForm.value.name!,
      this.contactForm.value.email!,
      this.contactForm.value.phone || '',
      this.contactForm.value.message!,
      (success) => {
        this.currentState = success ? ComponentState.Success : ComponentState.Error;
        setTimeout(() => {
          this.contactForm.enable();
          this.changeDetector.detectChanges();
        }, 100);
      }
    );
  }

  private ensureMapsReady(): Promise<void> {
    if (!this.isBrowser) return Promise.resolve();

    return this.loadGoogleMapsScript()
      .then(() =>
        this.waitFor(
          () => {
            const win = window as GoogleWindow;
            return !!(
              win.google?.maps &&
              win.google.maps.Map &&
              win.google.maps.marker?.AdvancedMarkerElement
            );
          },
          8000,
          50
        )
      );
  }

  private loadGoogleMapsScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      const srcMatch = 'maps.googleapis.com/maps/api/js';
      const existingScript = document.querySelector(`script[src*="${srcMatch}"]`) as ScriptWithPromise | null;
      const win = window as GoogleWindow;

      if (win.google?.maps) {
        resolve();
        return;
      }

      if (existingScript) {
        if (existingScript._gmapsReadyPromise) {
          existingScript._gmapsReadyPromise.then(resolve).catch(reject);
          return;
        }

        const promise = new Promise<void>((res, rej) => {
          existingScript.addEventListener('load', () => res(), { once: true });
          existingScript.addEventListener('error', (e) => rej(e), { once: true });
        });

        existingScript._gmapsReadyPromise = promise;
        promise.then(resolve).catch(reject);
        return;
      }

      const script: ScriptWithPromise = document.createElement('script') as ScriptWithPromise;
      script.src =
        'https://maps.googleapis.com/maps/api/js?key=AIzaSyBcZuSPINHEvo1BneBdeQ_q-kR7Xgaj-Hc&v=weekly&loading=async&libraries=maps,marker';
      script.async = true;
      script.defer = true;

      const readyPromise = new Promise<void>((res, rej) => {
        script.onload = () => res();
        script.onerror = (e) => rej(e);
      });

      script._gmapsReadyPromise = readyPromise;
      document.body.appendChild(script);

      readyPromise.then(resolve).catch(reject);
    });
  }

  private waitFor(cond: () => boolean, timeoutMs = 5000, intervalMs = 50): Promise<void> {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const tick = (): void => {
        if (cond()) return resolve();
        if (Date.now() - start >= timeoutMs) return reject(new Error('Timeout esperando Google Maps'));
        setTimeout(tick, intervalMs);
      };
      tick();
    });
  }

  private async loadGoogleMap(): Promise<void> {
    try {
      const win = window as GoogleWindow;
      const Map = win.google!.maps.Map;
      const AdvancedMarkerElement = win.google!.maps.marker!.AdvancedMarkerElement;

      this.initMap(Map, AdvancedMarkerElement);
    } catch (err) {
      console.error('Error loading Google Maps:', err);
    }
  }

  private initMap(
    Map: typeof google.maps.Map,
    AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement
  ): void {
    const location = { lat: 43.438463, lng: -3.854167 };
    const mapElement = document.getElementById('map');
    if (!mapElement) return;

    const map = new Map(mapElement, {
      zoom: 17,
      center: location,
      mapId: '7b174f2e67a4199b11323b5a',
      mapTypeControl: false,
      fullscreenControl: false,
      streetViewControl: false,
    });

    new AdvancedMarkerElement({
      map,
      position: location,
      title: 'Psicología Ana Pellón Mendoza',
    });

    setTimeout(() => {
      mapElement.classList.add('map-loaded');
      const infoBox = document.querySelector('.map-info') as HTMLElement | null;
      if (infoBox) infoBox.classList.add('map-info-visible');
    }, 50);
  }
}
